import { useContext } from "react";
import { Building2, Users } from "lucide-react";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuthStore } from "@/stores/authStore";
import { isSimpleOperator } from "@/stores/auth/auth.operator";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import { AccountSwitcher } from "@/components/account-switcher";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { ExchangeRateModal } from "@/components/Modals/ExchangeRateModal";
import { NotificationsButton } from "@/components/ExternalOrders/NotificationsButton";
import { KitchenReadyNotificationsButton } from "@/components/kitchen/KitchenReadyNotificationsButton";
import { useGetDollarValue } from "@/hooks/useGetDollarValue";
import HeaderMessage from "@/components/HeaderMessage";
import ProfileMenu from "@/components/ProfileMenu";
import { useLocation, useNavigate } from "react-router";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { rate, loading, refresh } = useGetDollarValue();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const activeOperator = useAuthStore((s) => s.activeOperator);
  const headerOrgLabel = currentOrganization?.name ?? translator("Home.noOrganizations");
  const showAccountSwitcher =
    location.pathname !== "/fast-switch" && !isSimpleOperator(activeOperator);

  const orgIndicator = (
    showAccountSwitcher ? (
      <AccountSwitcher />
    ) : (
      <div className="flex items-center gap-2 py-1.5 min-w-0 text-foreground">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium truncate max-w-[min(200px,40vw)] sm:max-w-[240px]">
          {headerOrgLabel}
        </span>
      </div>
    )
  );

  return (
    <header className="border-b border-sidebar-border bg-header dark:border-border dark:bg-card w-full">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <img
              src={`${import.meta.env.BASE_URL}images/tabstr-logo-transparent.png`}
              alt="Tabstr logo"
              className="h-9 sm:h-11 w-auto min-w-0 object-contain object-left sm:shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                (e.currentTarget
                  .nextElementSibling as HTMLElement)!.style.display =
                  "inline-block";
              }}
            />
            <div
              className="text-lg sm:text-xl font-bold text-foreground shrink-0"
              style={{ display: "none" }}
            >
              tabstr.
            </div>
            <div className="hidden sm:block min-w-0">{orgIndicator}</div>
          </div>

          {/* shown on larger screens */}
          <div className="hidden sm:block">
            <HeaderMessage />
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center space-x-2 max-[359px]:space-x-1.5 lg:space-x-3">
              <NotificationsButton />
              {location.pathname !== "/kitchen" && <KitchenReadyNotificationsButton />}
              <div className="hidden sm:block">
                <ExchangeRateModal
                  rate={rate}
                  loading={loading}
                  onRefresh={refresh}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="default"
                className="h-10 touch-manipulation px-3"
                onClick={() => navigate("/fast-switch", { replace: true })}
              >
                <Users className="h-4 w-4" />
              </Button>
              <ThemeToggle />
              <ProfileMenu />
            </div>
          </div>
        </div>

        <div className="sm:hidden mt-2 min-w-0">{orgIndicator}</div>

        {/* Mobile message display */}
        <div className="sm:hidden mt-2">
          <HeaderMessage />
        </div>

        {/* Medium desktop layout - show name on tablet */}
        <div className="hidden sm:block lg:hidden mt-2">
          <div className="text-center text-sm text-muted-foreground">
          </div>
        </div>
      </div>
    </header>
  );
}
